import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import { buildProductHierarchy } from 'src/app/_helpers/product-levels';
import { AgentService } from 'src/app/_services/agent.service';
import { CommissionService } from 'src/app/_services/commission.service';
import { Lead } from 'src/app/_types/lead.types';
import { TokenPartner } from 'src/app/_types/partner.type';
import { Category, Product, SubCategory } from 'src/app/_types/product.types';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';
import { PartnerType } from 'src/app/enums/partner.enums';
import { Periodicity } from 'src/app/enums/proposal.enums';
import { Globals } from 'src/app/globals';
import { LanguageCode } from 'src/app/helpers/nationalities';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { TreeComponent, TreeNode } from '../../tree/tree.component';

/**
 * Interface defining the structure of a single product item in the productList FormArray.
 * Each product item contains product information and user-entered data (like amount).
 */
export interface ProductItemFormGroup {
  productId: FormControl<string>;
  displayProductName: FormControl<string>;
  productName: FormControl<string>;
  productNameInFrench: FormControl<string>;
  coverOrder?: FormControl<number | null | undefined>;
  score?: FormControl<number | null | undefined>;
  rootProductId?: FormControl<string | null | undefined>;
  mainProductId?: FormControl<string | null | undefined>;
  benefits?: FormControl<string | null | undefined>;
  amount: FormControl<number>;
}

/**
 * Interface defining the complete structure of the products form group.
 * This form group manages category, subcategory, and product selection,
 * along with associated metadata and the list of selected products.
 */
export interface ProductsFormGroupNewProposal {
  partnerId: FormControl<string>;
  categoryId: FormControl<string | null>;
  subCategoryId: FormControl<string | null>;
  categoryName: FormControl<string | null>;
  categoryNameInFrench: FormControl<string | null>;
  categoryLogo: FormControl<string | null>;
  subCategoryName: FormControl<string | null>;
  subCategoryNameInFrench: FormControl<string | null>;
  product: FormControl<string[]>;
  agent: FormControl<string | null>;
  periodicity: FormControl<string | null>;
  coversPeriodicity: FormControl<Periodicity>;
  premiumAmount: FormControl<number>;
  totalAmount: FormControl<number>;
  productList: FormArray<FormGroup<ProductItemFormGroup>>;
}

export interface ProductsFormGroupExistingPolicy {
  policyId: FormControl<string>;
  categoryId: FormControl<string | null>;
  subCategoryId: FormControl<string | null>;
  agentId: FormControl<string | null>;
  product: FormControl<string[]>;
  insuranceType: FormControl<InsuranceType>;
  totalAmount: FormControl<string>;
  premiumAmount: FormControl<string>;
  periodicity: FormControl<Periodicity | null>;
  coversPeriodicity: FormControl<Periodicity>;
  startDate: FormControl<Date>;
  endDate: FormControl<Date>;
  productList: FormArray<FormGroup<ProductItemFormGroup>>;
  familyMemberId: FormControl<string>;
  categoryName: FormControl<string | null>;
  categoryNameInFrench: FormControl<string | null>;
  categoryLogo: FormControl<string | null>;
  subCategoryName: FormControl<string | null>;
  subCategoryNameInFrench: FormControl<string | null>;
}
/**
 * ProductSelectionComponent handles the selection of insurance products through a hierarchical tree structure.
 *
 * Features:
 * - Displays products in a tree hierarchy (category -> subcategory -> products)
 * - Allows multi-selection of products via checkboxes
 * - Supports search functionality with debouncing
 * - Maintains selected products even when filtered out by search
 * - Auto-selects parent nodes when child nodes are selected
 * - Auto-deselects child nodes when parent nodes are deselected
 * - Preserves user-entered amounts when updating product selections
 * - Supports initialization from existing form data (for updates)
 */
@Component({
  selector: 'app-product-selection',
  templateUrl: './product-selection.component.html',
  styleUrls: ['./product-selection.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TreeComponent,
    IconsModule,
  ],
})
export class ProductSelectionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _insuranceType: InsuranceType;

  @Input() set insuranceType(value: InsuranceType) {
    this._insuranceType = value;
    this._getCategoryList(false, false);
  }

  get insuranceType() {
    return this._insuranceType;
  }

  @Input() partnerId: string;

  @Input() agentId?: string;

  @Input() insurer?: string | null;

  @Input() leadDetailsData?: Lead;

  @Input() leadId?: string;

  /** The parent form group containing all product-related form controls */
  @Input() productForm!:
    | FormGroup<ProductsFormGroupNewProposal>
    | FormGroup<ProductsFormGroupExistingPolicy>;

  /** Emitted when product selection changes in the tree */
  @Output() productSelectionChanged = new EventEmitter();

  /** Current language code (fr/en) for displaying localized product names */
  lngCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';

  /** Internal storage for category data */
  private _categoryData: Category[] = [];

  /** Insurance type of the latest category request: older responses are ignored */
  private latestCategoryInsuranceType?: InsuranceType;

  /**
   * Getter for category data.
   * Returns the sorted list of categories.
   */
  get categoryData() {
    return this._categoryData;
  }

  /**
   * Setter for category data.
   * Automatically sorts categories by name based on current language.
   */
  set categoryData(value: Category[]) {
    this._categoryData = value;
    if (value) {
      this.categoryData.sort((a, b) => {
        if (this.lngCode === 'en')
          return a.categoryName
            ?.toLowerCase()
            .localeCompare(b.categoryName?.toLowerCase());
        return a.categoryNameInFrench
          ?.toLowerCase()
          .localeCompare(b.categoryNameInFrench?.toLowerCase());
      });
    }
  }

  /** Internal storage for subcategory data */
  private _subCategoryData: SubCategory[] = [];

  /**
   * Getter for subcategory data.
   * Returns the sorted list of subcategories.
   */
  get subCategoryData() {
    return this._subCategoryData;
  }

  /**
   * Setter for subcategory data.
   * Automatically sorts subcategories by name based on current language.
   */
  set subCategoryData(value: SubCategory[]) {
    this._subCategoryData = value;
    if (value) {
      this.subCategoryData.sort((a, b) => {
        if (this.lngCode === 'en')
          return a.subCategoryName
            ?.toLowerCase()
            .localeCompare(b.subCategoryName?.toLowerCase());
        return a.subCategoryNameInFrench
          ?.toLowerCase()
          .localeCompare(b.subCategoryNameInFrench?.toLowerCase());
      });
    }
  }

  /** Internal storage for product data */
  private _productData: Product[] = [];

  /**
   * Getter for product data.
   * Returns the sorted list of products.
   */
  get productData() {
    return this._productData;
  }

  /**
   * Setter for product data.
   * Automatically sorts products by name and rebuilds the product tree when data changes.
   */
  set productData(value: Product[]) {
    this._productData = value;
    if (value) {
      this._productData.sort((a, b) => {
        if (this.lngCode === 'en')
          return a.productName
            ?.toLowerCase()
            .localeCompare(b.productName?.toLowerCase());
        return a.productNameInFrench
          ?.toLowerCase()
          .localeCompare(b.productNameInFrench?.toLowerCase());
      });
      this.buildProductTree();
    }
  }

  /** Complete product tree structure built from product hierarchy */
  productTreeData: TreeNode[] = [];

  /** Filtered product tree based on search criteria (includes selected products even if hidden) */
  filteredProductTreeData: TreeNode[] = [];

  /** Set of selected product IDs (maintains selection even when products are filtered out) */
  selectedProductIds: Set<string> = new Set<string>();

  /** Subscription to category form control value changes */
  categorySubscription!: Subscription;

  /** Subscription to subcategory form control value changes */
  subcategorySubscription!: Subscription;

  user!: TokenPartner;

  constructor(
    private readonly fb: FormBuilder,
    private readonly agentService: AgentService,
    private readonly commissionService: CommissionService,
    private readonly toastrService: ToastrService,
    private readonly cookieService: CookieService,
    private readonly globals: Globals
  ) {
    this.user = globals.getUser();
  }

  ngOnInit(): void {
    // Initialize selected products from form if component is being used for updates
    // This ensures that existing selections are preserved when the component loads
    if (this.productList && this.productList.length > 0) {
      const selectedProductIds = this.productList.controls.map(
        (control) => control.value.productId
      );
      selectedProductIds.forEach((productId) => {
        this.selectedProductIds.add(productId);
      });
    }

    // Subscribe to search input changes with debouncing (300ms delay)
    // This prevents excessive filtering while user is typing
    this.searchSubscription = this.searchFormControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value) => {
        if (!value || value.trim() === '') {
          this.filteredProductTreeData = this.productTreeData;
        } else {
          this.onProductSearch(value);
        }
      });
  }

  ngAfterViewInit(): void {
    // Subscribe to category selection changes
    // When a category is selected, automatically load its subcategories
    this.categorySubscription = this.productForm.controls[
      'categoryId'
    ].valueChanges.subscribe((value) => {
      if (!value) return;
      const category = this.categoryData.find((x) => x.categoryId === value);
      const { insuranceType } = this;
      if (
        !insuranceType ||
        !category?.insuranceTypes.some((x) => x.toString() === insuranceType)
      ) {
        // Auto-select first insurance type if needed
        if (category?.insuranceTypes.length > 0) {
          // This would need to be handled by parent component
        }
      }
      this._getSubCategoryList(value);
    });

    // Subscribe to subcategory selection changes
    // When a subcategory is selected, automatically load its products
    this.subcategorySubscription = this.productForm.controls[
      'subCategoryId'
    ].valueChanges.subscribe((value) => {
      if (!value) return;
      const subcategory = this.subCategoryData.find(
        (x) => x.subCategoryId === value
      );
      const { insuranceType } = this;
      if (
        (!insuranceType ||
          !subcategory?.insuranceTypes.some(
            (x: InsuranceType) => x.toString() === insuranceType
          )) &&
        this.formControls['insuranceType'] &&
        !this.formControls['insuranceType'].value
      ) {
        // If the insuranceType was not set, we set it from the subcategory when possible
        this.formControls['insuranceType'].setValue(
          subcategory?.insuranceTypes?.[0]
        );
      }
      this._getProductList(value);
    });

    // Load categories if insurance type is available
    if (this.insuranceType) {
      this._getCategoryList();
    }
  }

  ngOnDestroy(): void {
    if (this.categorySubscription) this.categorySubscription.unsubscribe();
    if (this.subcategorySubscription)
      this.subcategorySubscription.unsubscribe();
  }

  /**
   * Getter for the productList FormArray.
   * This array contains all selected products with their associated data (amounts, etc.)
   */
  get productList(): FormArray<FormGroup<ProductItemFormGroup>> {
    return this.productForm.controls.productList as FormArray<
      FormGroup<ProductItemFormGroup>
    >;
  }

  /**
   * Getter for form controls (convenience method for template access)
   */
  get formControls() {
    return this.productForm.controls;
  }

  // ==================== Category Methods ====================

  /**
   * Fetches the list of categories based on the current context (agent, partner, or general).
   *
   * @param emitEvent - Whether to emit form value change events when patching values
   * @param prefill - Whether to automatically prefill category based on lead metadata
   * @param insuranceType - Insurance type the insurers are filtered by (defaults to the input)
   * @returns Observable that emits true on success, false on error. A response superseded by a
   * request for another insurance type is ignored and emits true.
   *
   * Logic:
   * - If agentId is available: fetches agent-specific categories
   * - If partnerId is available: fetches partner-specific categories
   * - Otherwise: fetches all categories filtered by insurance type
   */
  _getCategoryList(
    emitEvent: boolean = true,
    prefill = true,
    insuranceType: InsuranceType = this.insuranceType
  ): Observable<boolean> {
    const res = new Subject<boolean>();
    this.latestCategoryInsuranceType = insuranceType;
    const isStale = () =>
      (insuranceType ?? null) !== (this.latestCategoryInsuranceType ?? null);
    (this.user.userType === PartnerType.PARTNER
      ? this.commissionService._getFeeCategory(insuranceType)
      : this.commissionService._getAgentCategory({ insuranceType })
    ).subscribe({
      next: (result) => {
        if (isStale()) {
          res.next(true);
          res.complete();
        } else if (result.status == 'SUCCESS') {
          this.categoryData = result.data.categoryList;
          if (this.insurer?.trim() || prefill)
            this.findChosenInsurer(emitEvent);
          res.next(true);
          res.complete();
        } else {
          this.toastrService.error(result.status);
          res.next(false);
          res.complete();
        }
      },
      error: (err: any) => {
        if (isStale()) {
          res.next(true);
          res.complete();
          return;
        }
        this.toastrService.error(
          err?.error?.error?.clientErrorMessage ??
            $localize`Une erreur est survenue`
        );
        res.next(false);
        res.complete();
      },
    });
    return res;
  }

  /**
   * Attempts to automatically select a category (insurer) based on lead metadata.
   * This is used when a lead has a chosen base offer with a specific insurance name.
   *
   * @param emitEvent - Whether to emit form value change events
   */
  findChosenInsurer(emitEvent: boolean = true) {
    const chosenOfferInsuranceName =
      this.leadDetailsData?.metadata &&
      'chosenBaseOffer' in this.leadDetailsData.metadata
        ? this.leadDetailsData.metadata.chosenBaseOffer?.insuranceName
        : undefined;
    const insuranceName =
      this.insurer?.trim() || chosenOfferInsuranceName?.trim();
    if (!insuranceName) {
      return;
    }

    const normalizedInsuranceName = insuranceName.toLowerCase();
    const insurer = this.categoryData.find((category) =>
      [category.categoryName, category.categoryNameInFrench].some(
        (categoryName) => {
          const normalizedCategoryName = categoryName?.trim().toLowerCase();
          return (
            !!normalizedCategoryName &&
            (normalizedCategoryName === normalizedInsuranceName ||
              normalizedInsuranceName.includes(normalizedCategoryName) ||
              normalizedCategoryName.includes(normalizedInsuranceName))
          );
        }
      )
    );
    if (!insurer) {
      return;
    }
    this.productForm.patchValue(
      {
        categoryId: insurer.categoryId,
        categoryName: insurer.categoryName,
        categoryNameInFrench: insurer.categoryNameInFrench,
      },
      { emitEvent }
    );

    this._getSubCategoryList(insurer.categoryId, emitEvent);
  }

  // ==================== Subcategory Methods ====================

  /**
   * Fetches the list of subcategories for a given category.
   *
   * @param selectedCategoryId - The ID of the selected category
   * @param emitEvent - Whether to emit form value change events
   * @param prefill - Whether to automatically prefill subcategory based on lead metadata
   * @returns Observable that emits true on success, false on error
   *
   * Note: When a category is selected, this method also resets the product list
   * to prevent stale data from the previous category.
   */
  _getSubCategoryList(
    selectedCategoryId: string,
    emitEvent: boolean = true,
    prefill = true
  ): Observable<boolean> {
    const res = new Subject<boolean>();
    const selectedCategory = this.categoryData.find(
      (category) => category.categoryId === selectedCategoryId
    );
    const catObj = {
      categoryName: selectedCategory?.categoryName,
      categoryNameInFrench: selectedCategory?.categoryNameInFrench,
      categoryLogo: selectedCategory?.logo,
    };
    this.productForm.patchValue(catObj, { emitEvent });

    if (emitEvent) this.resetProducts();

    const obj = {
      categoryId: selectedCategoryId,
      insuranceType: this.getInsuranceType(),
      agent: this.user.userType === PartnerType.AGENT,
      partnerId: this.user.partnerId,
    };
    this.commissionService._getFeeSubCategory(obj).subscribe({
      next: (result) => {
        if (result.status === 'SUCCESS') {
          this.subCategoryData = result.data.subCategoryList;
          if (prefill) this.findChosenSubcategory(emitEvent);
          res.next(true);
        } else {
          this.toastrService.error(result.status, '');
          res.next(false);
        }
      },
      error: (err: any) => {
        this.toastrService.error(
          err?.error?.error?.clientErrorMessage ??
            $localize`Une erreur est survenue`
        );
        res.next(false);
      },
    });
    return res;
  }

  /**
   * Attempts to automatically select a subcategory based on the insurance type from lead metadata.
   * Maps insurance types to their corresponding French subcategory names.
   *
   * @param emitEvent - Whether to emit form value change events
   */
  findChosenSubcategory(emitEvent: boolean = true) {
    let subCategoryName = '';
    switch (this.leadDetailsData?.insuranceType) {
      case InsuranceType.LEGAL_PROTECTION:
        subCategoryName = 'Protection juridique';
        break;
      case InsuranceType.BUILDING:
        subCategoryName = 'Bâtiment';
        break;
      case InsuranceType.MOTOR_VEHICLE:
        subCategoryName = 'Voiture';
        break;
      case InsuranceType.ANIMAL:
        subCategoryName = 'Animal';
        break;
      case InsuranceType.RC_HOUSEHOLD:
        subCategoryName = 'RC Ménage';
        break;
      case InsuranceType.TRAVEL:
        subCategoryName = 'Voyage';
        break;
      case InsuranceType.HEALTH:
        subCategoryName = 'Santé';
    }

    const selectedSubCategory = this.subCategoryData.find(
      (subCategory) => subCategory.subCategoryNameInFrench === subCategoryName
    );

    this.productForm.patchValue(
      {
        subCategoryId: selectedSubCategory?.subCategoryId,
        subCategoryName: selectedSubCategory?.subCategoryName,
        subCategoryNameInFrench: selectedSubCategory?.subCategoryNameInFrench,
      },
      { emitEvent }
    );

    if (selectedSubCategory?.subCategoryId)
      this._getProductList(selectedSubCategory?.subCategoryId, emitEvent);
  }

  // ==================== Product Methods ====================

  /**
   * Fetches the list of products for a given subcategory.
   *
   * @param subCategoryId - The ID of the selected subcategory
   * @param emitEvent - Whether to emit form value change events
   * @param prefill - Whether to automatically prefill products based on lead metadata
   * @returns Observable that emits true on success, false on error
   *
   * Note: When emitEvent is true, this clears the existing product list
   * to prevent mixing products from different subcategories.
   */
  _getProductList(
    subCategoryId = '',
    emitEvent: boolean = true,
    prefill = true
  ): Observable<boolean> {
    const res = new Subject<boolean>();
    const selectedSubcategory = this.subCategoryData.find(
      (subCategory) => subCategory.subCategoryId === subCategoryId
    );
    this.productForm.patchValue(
      {
        subCategoryName: selectedSubcategory?.subCategoryName,
        subCategoryNameInFrench: selectedSubcategory?.subCategoryNameInFrench,
      },
      { emitEvent }
    );

    const obj = {
      subCategoryId,
      withSubProducts: true,
      agent: this.user.userType === PartnerType.AGENT,
    };
    this.commissionService._getAllProposalProduct(obj, true).subscribe({
      next: (result) => {
        if (emitEvent) this.productList.clear();
        if (result.status == 'SUCCESS') {
          this.productData = result.data.productList;
          if (prefill) this.findChosenProduct(emitEvent);
          res.next(true);
        } else {
          this.toastrService.error(result.status);
          res.next(false);
        }
      },
      error: (err: any) => {
        this.toastrService.error(
          err?.error?.error?.clientErrorMessage ??
            $localize`Une erreur est survenue`
        );
        res.next(false);
      },
    });
    return res;
  }

  /**
   * Attempts to automatically select products based on lead metadata.
   * Matches product names from the chosen base offer with available products.
   *
   * @param emitEvent - Whether to emit form value change events
   */
  findChosenProduct(emitEvent: boolean = true) {
    if (
      this.leadDetailsData?.metadata &&
      'chosenBaseOffer' in this.leadDetailsData.metadata
    ) {
      const products =
        this.leadDetailsData.metadata.chosenBaseOffer?.productNames ?? [];

      const selectedProductIds: string[] = [];
      products.forEach((product) => {
        const productData = this.productData.find(
          (p) => p.productNameInFrench === product
        );
        if (productData) {
          selectedProductIds.push(productData.productId);
        }
      });

      if (selectedProductIds.length > 0 && this.productTreeData.length > 0) {
        const selectedNodes = this.findTreeNodesByIds(
          this.productTreeData,
          selectedProductIds
        );
        if (selectedNodes.length > 0) {
          this.onProductSelectionChanged(selectedNodes);
        }
      }
    }
  }

  // ==================== Tree and Search Methods ====================

  /**
   * Builds the product tree structure from flat product data.
   * Converts the hierarchical product structure into TreeNode format for display.
   * Also applies current search filter if active and syncs selected products.
   */
  private buildProductTree() {
    if (!this._productData || this._productData.length === 0) {
      this.productTreeData = [];
      this.filteredProductTreeData = [];
      return;
    }

    const hierarchy = buildProductHierarchy(this._productData);
    this.productTreeData = this.convertProductsToTreeNodes(hierarchy);

    if (
      this.searchFormControl.value &&
      this.searchFormControl.value.trim() !== ''
    ) {
      this.onProductSearch(this.searchFormControl.value);
    } else {
      this.filteredProductTreeData = this.productTreeData;
    }

    this.syncSelectedProductsToTree();
  }

  /**
   * Synchronizes selected products from the form's productList FormArray to the tree selection state.
   * This ensures that products selected in the form (e.g., during updates) are reflected in the tree.
   * Also updates the product FormControl with the names of selected products.
   */
  private syncSelectedProductsToTree() {
    if (this.productList && this.productList.length > 0) {
      const selectedProductIds = this.productList.controls.map(
        (control) => control.value.productId
      );

      const selectedNodes = this.findTreeNodesByIds(
        this.productTreeData,
        selectedProductIds
      );
      this.selectedProductIds = new Set(selectedProductIds);

      const selectedProductNames = selectedNodes.map((node) => {
        const product = this._productData.find((p) => p.productId === node.id);
        return this.lngCode === 'fr'
          ? (product?.productNameInFrench ?? node.name)
          : (product?.productName ?? node.name);
      });

      this.productForm.controls['product'].setValue(selectedProductNames, {
        emitEvent: false,
      });
    }
  }

  /**
   * Recursively finds tree nodes by their IDs.
   *
   * @param nodes - The tree nodes to search through
   * @param ids - Array of node IDs to find
   * @returns Array of matching TreeNode objects
   */
  private findTreeNodesByIds(nodes: TreeNode[], ids: string[]): TreeNode[] {
    const result: TreeNode[] = [];
    const idSet = new Set(ids);

    const traverse = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        if (idSet.has(node.id)) {
          result.push(node);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };

    traverse(nodes);
    return result;
  }

  /**
   * Converts the hierarchical product structure into TreeNode format for the tree component.
   * Recursively processes products and their nested children.
   *
   * @param products - Hierarchical product structure from buildProductHierarchy
   * @returns Array of TreeNode objects ready for tree display
   */
  private convertProductsToTreeNodes(
    products: Array<
      Product & { products: Array<Product & { products: Product[] }> }
    >
  ): TreeNode[] {
    return products.map((product) => {
      const node: TreeNode = {
        id: product.productId,
        name:
          this.lngCode === 'en'
            ? (product.productName ?? product.productNameInFrench)
            : (product.productNameInFrench ?? product.productName),
        children:
          product.products && product.products.length > 0
            ? this.convertProductsToTreeNodes(product.products as any)
            : undefined,
      };
      return node;
    });
  }

  /**
   * Handles product selection changes from the tree component.
   *
   * Key behaviors:
   * - Merges new selections with existing selections (preserves hidden selections)
   * - Ensures only one subproduct can be selected per main product
   * - Updates the product FormControl with selected product names
   * - Updates the productList FormArray with selected products
   * - Emits productSelectionChanged event to notify parent component
   *
   * @param selectedNodes - Array of TreeNode objects that are currently selected in the visible tree
   */
  onProductSelectionChanged(selectedNodes: TreeNode[]) {
    const previousSelectedIds = new Set(this.selectedProductIds);

    const visibleNodeIds = new Set<string>();
    const collectVisibleIds = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        visibleNodeIds.add(node.id);
        if (node.children && node.children.length > 0) {
          collectVisibleIds(node.children);
        }
      });
    };
    collectVisibleIds(this.filteredProductTreeData);

    const newSelectedIds = new Set(selectedNodes.map((node) => node.id));
    const mergedSelectedIds = new Set<string>();

    this.selectedProductIds.forEach((id) => {
      if (!visibleNodeIds.has(id)) {
        mergedSelectedIds.add(id);
      }
    });

    newSelectedIds.forEach((id) => {
      mergedSelectedIds.add(id);
    });

    // Ensure only one subproduct per main product:
    // if the user selects a second subproduct under the same main product, we keep the newly added one
    // and remove the previously selected sibling subproduct(s).
    const newlyAddedIds = Array.from(mergedSelectedIds).filter(
      (id) => !previousSelectedIds.has(id)
    );

    newlyAddedIds.forEach((addedId) => {
      // If this id was already removed by a previous pass, skip
      if (!mergedSelectedIds.has(addedId)) return;

      const addedProduct = this._productData.find(
        (p) => p.productId === addedId
      );
      if (
        !addedProduct ||
        !addedProduct.mainProductId ||
        !addedProduct.rootProductId ||
        addedProduct.mainProductId === addedProduct.rootProductId
      ) {
        return;
      }

      const subproductsToRemove: string[] = [];
      mergedSelectedIds.forEach((selectedId) => {
        if (selectedId === addedId) return;
        const selectedProduct = this._productData.find(
          (p) => p.productId === selectedId
        );
        if (
          selectedProduct &&
          selectedProduct.mainProductId &&
          selectedProduct.rootProductId &&
          selectedProduct.mainProductId !== selectedProduct.rootProductId &&
          selectedProduct.mainProductId === addedProduct.mainProductId
        ) {
          subproductsToRemove.push(selectedId);
        }
      });

      subproductsToRemove.forEach((id) => mergedSelectedIds.delete(id));
    });

    this.selectedProductIds = mergedSelectedIds;

    const allSelectedProducts: string[] = [];
    this.selectedProductIds.forEach((productId) => {
      const product = this._productData.find((p) => p.productId === productId);
      if (product) {
        const productName =
          this.lngCode === 'fr'
            ? (product.productNameInFrench ?? product.productName)
            : (product.productName ?? product.productNameInFrench);
        allSelectedProducts.push(productName);
      }
    });

    this.productForm.controls['product'].setValue(allSelectedProducts);
    this.updateProductListFormArray(selectedNodes);
    this.productSelectionChanged.emit();
  }

  /**
   * Updates the productList FormArray to match the current selection.
   *
   * Important behaviors:
   * - Adds new products with empty amounts (user must enter amounts)
   * - Updates existing products' metadata (name, coverOrder, etc.) while preserving amounts
   * - Removes products that are no longer selected
   * - Preserves user-entered amounts when updating product information
   * - When a product has mainProductId !== rootProductId, appends its name to the main product
   *
   * @param selectedNodes - Currently selected nodes (used for reference, but allSelectedIds is the source of truth)
   */
  private updateProductListFormArray(selectedNodes: TreeNode[]) {
    const allSelectedIds = new Set(this.selectedProductIds);

    // First pass: add/update all products in the FormArray
    allSelectedIds.forEach((productId) => {
      const product = this._productData.find((p) => p.productId === productId);
      if (product) {
        const existingIndex = this.productList.controls.findIndex(
          (control) => control.value.productId === product.productId
        );

        if (existingIndex === -1) {
          // New product - add it with empty amount
          const productFormGroup = this.fb.group({
            productId: [product.productId, [Validators.required]],
            productName: [
              this.lngCode === 'en'
                ? (product.productName ?? product.productNameInFrench)
                : (product.productNameInFrench ?? product.productName),
              [Validators.required],
            ],
            productNameInFrench: [
              product.productNameInFrench,
              [Validators.required],
            ],
            coverOrder: [product.coverOrder ?? null],
            score: [product.score ?? null],
            rootProductId: [product.rootProductId ?? null],
            mainProductId: [product.mainProductId ?? null],
            displayProductName: [
              this.lngCode === 'en'
                ? (product.productName ?? product.productNameInFrench)
                : (product.productNameInFrench ?? product.productName),
            ],
            amount: [0, [Validators.required]],
            benefits: [product.benefits ?? null],
          });
          this.productList.push(
            productFormGroup as FormGroup<ProductItemFormGroup>
          );
        } else {
          // Existing product - update product info but preserve amount and other fields
          const existingControl = this.productList.controls[
            existingIndex
          ] as FormGroup;

          // Only update name if this product is not being appended to a main product
          const shouldUpdateName = !(
            product.mainProductId &&
            product.rootProductId &&
            product.mainProductId !== product.rootProductId
          );

          existingControl.patchValue({
            ...(shouldUpdateName && {
              displayProductName:
                this.lngCode === 'en'
                  ? (product.productName ?? product.productNameInFrench)
                  : (product.productNameInFrench ?? product.productName),
            }),
            productName: product.productName,
            productNameInFrench: product.productNameInFrench,
            coverOrder: product.coverOrder,
            score: product.score,
            rootProductId: product.rootProductId,
            mainProductId: product.mainProductId,
            benefits: product.benefits,
          });
          // Amount is preserved - don't update it
        }
      }
    });

    // Remove products that are no longer selected
    for (let i = this.productList.controls.length - 1; i >= 0; i--) {
      const { productId } = this.productList.controls[i].value;
      if (!allSelectedIds.has(productId)) {
        this.productList.removeAt(i);
      }
    }

    // Second pass: rebuild main product names by appending selected sub-product names
    // Group products by their mainProductId
    // Only one subproduct per main product is allowed, so we only use the first one if multiple exist
    const productsByMainProduct = new Map<string, Product[]>();

    allSelectedIds.forEach((productId) => {
      const product = this._productData.find((p) => p.productId === productId);
      if (
        product &&
        product.mainProductId &&
        product.rootProductId &&
        product.mainProductId !== product.rootProductId
      ) {
        if (!productsByMainProduct.has(product.mainProductId)) {
          productsByMainProduct.set(product.mainProductId, []);
        }
        productsByMainProduct.get(product.mainProductId)!.push(product);
      }
    });

    // Update main product names with appended sub-product names
    // Only use the first subproduct if multiple exist (shouldn't happen, but handle gracefully)
    productsByMainProduct.forEach((subProducts, mainProductId) => {
      const mainProductIndex = this.productList.controls.findIndex(
        (control) => control.value.productId === mainProductId
      );

      if (mainProductIndex !== -1 && subProducts.length > 0) {
        const mainProductControl = this.productList.controls[
          mainProductIndex
        ] as FormGroup<ProductItemFormGroup>;
        const mainProduct = this._productData.find(
          (p) => p.productId === mainProductId
        );

        if (mainProduct) {
          // Start with the original main product name
          let mainProductName =
            this.lngCode === 'en'
              ? (mainProduct.productName ?? mainProduct.productNameInFrench)
              : (mainProduct.productNameInFrench ?? mainProduct.productName);

          // Only append the first sub-product name (only one subproduct per main product allowed)
          const firstSubProduct = subProducts[0];
          const subProductName =
            this.lngCode === 'en'
              ? (firstSubProduct.productName ??
                firstSubProduct.productNameInFrench)
              : (firstSubProduct.productNameInFrench ??
                firstSubProduct.productName);
          mainProductName = `${mainProductName} - ${subProductName}`;
          mainProductControl.patchValue(
            {
              displayProductName: mainProductName,
            },
            { emitEvent: true }
          );
        }
      }
    });
  }

  /**
   * Converts the Set of selected product IDs to an array.
   * Useful for API calls or other operations that require an array format.
   */
  selectedProductIdsAsArray(): string[] {
    return Array.from(this.selectedProductIds);
  }

  /**
   * Checks if a product should be displayed in the UI.
   * Products with mainProductId !== rootProductId should not be displayed
   * (they are appended to their main product instead).
   */
  shouldDisplayProduct(
    productControl: FormGroup<ProductItemFormGroup>
  ): boolean {
    const { mainProductId } = productControl.value;
    const { rootProductId } = productControl.value;

    // Display if mainProductId is not set, or if mainProductId equals rootProductId
    if (!mainProductId || !rootProductId) {
      return true;
    }

    return mainProductId === rootProductId;
  }

  /**
   * Returns the list of product controls that should be displayed in the UI.
   * Filters out products where mainProductId !== rootProductId.
   */
  get displayableProductControls(): FormGroup<ProductItemFormGroup>[] {
    if (!this.productList) {
      return [];
    }
    return this.productList.controls.filter((control) =>
      this.shouldDisplayProduct(control)
    );
  }

  /**
   * Helper function to get the display name of a tree node.
   * Used by the tree component for rendering.
   */
  getProductNodeName = (node: TreeNode): string => node.name;

  /**
   * Filters the product tree based on search text.
   *
   * Search behavior:
   * - Matches product names (case-insensitive)
   * - Includes parent nodes of matching nodes (to show hierarchy)
   * - Always includes selected products and their parents (even if they don't match search)
   * - This ensures users can see and interact with their selections even when filtered
   *
   * @param searchText - The search query string
   */
  onProductSearch(searchText: string) {
    if (!searchText || searchText.trim() === '') {
      this.filteredProductTreeData = this.productTreeData;
      return;
    }

    const searchLower = searchText.toLowerCase().trim();
    const matchingNodeIds = new Set<string>();

    const findMatchingNodes = (
      nodes: TreeNode[],
      parentPath: TreeNode[] = []
    ): void => {
      for (const node of nodes) {
        const currentPath = [...parentPath, node];
        const nodeName = node.name.toLowerCase();

        if (nodeName.includes(searchLower)) {
          currentPath.forEach((pathNode) => matchingNodeIds.add(pathNode.id));
        }

        if (node.children && node.children.length > 0) {
          findMatchingNodes(node.children, currentPath);
        }
      }
    };

    findMatchingNodes(this.productTreeData);

    this.selectedProductIds.forEach((selectedId) => {
      const selectedNodePath = this.findNodePathById(
        this.productTreeData,
        selectedId
      );
      if (selectedNodePath.length > 0) {
        selectedNodePath.forEach((node) => matchingNodeIds.add(node.id));
      }
    });

    this.filteredProductTreeData = this.filterTreeByNodeIds(
      this.productTreeData,
      matchingNodeIds
    );
  }

  /**
   * Finds the path from root to a target node in the tree.
   * Used to include parent nodes when a selected product is filtered out.
   *
   * @param nodes - The tree nodes to search
   * @param targetId - The ID of the target node
   * @returns Array of TreeNode objects representing the path from root to target
   */
  private findNodePathById(nodes: TreeNode[], targetId: string): TreeNode[] {
    const path: TreeNode[] = [];

    const findPath = (
      nodeList: TreeNode[],
      currentPath: TreeNode[]
    ): boolean => {
      for (const node of nodeList) {
        const newPath = [...currentPath, node];

        if (node.id === targetId) {
          path.push(...newPath);
          return true;
        }

        if (node.children && node.children.length > 0) {
          if (findPath(node.children, newPath)) {
            return true;
          }
        }
      }
      return false;
    };

    findPath(nodes, []);
    return path;
  }

  private filterTreeByNodeIds(
    nodes: TreeNode[],
    matchingIds: Set<string>
  ): TreeNode[] {
    const filtered: TreeNode[] = [];

    for (const node of nodes) {
      const filteredNode: TreeNode = {
        id: node.id,
        name: node.name,
        children: undefined,
      };

      let hasMatchingDescendants = false;
      if (node.children && node.children.length > 0) {
        const filteredChildren = this.filterTreeByNodeIds(
          node.children,
          matchingIds
        );
        if (filteredChildren.length > 0) {
          filteredNode.children = filteredChildren;
          hasMatchingDescendants = true;
        }
      }

      if (matchingIds.has(node.id) || hasMatchingDescendants) {
        filtered.push(filteredNode);
      }
    }

    return filtered;
  }

  clearProductSearch() {
    this.searchFormControl.setValue('', { emitEvent: false });
    this.filteredProductTreeData = this.productTreeData;
  }

  private resetProducts() {
    this.productData = [];
    this.productForm.controls.product.reset();
    this.productList.clear();
    this.searchFormControl.setValue('', { emitEvent: false });
    this.filteredProductTreeData = [];
  }

  /**
   * Initialize the component from the form group values.
   * This method should be called when updating an existing proposal.
   * It loads categories, subcategories, and products based on the form group values.
   *
   * @param applyProductFusion - Whether to append subproduct names to imported main products once loaded
   * @param insuranceType - Type to load categories for; pass it when the form was just patched,
   *   since the `insuranceType` input only updates on the next change detection
   */
  initializeFromFormGroup(
    applyProductFusion: boolean = false,
    insuranceType: InsuranceType = this.insuranceType
  ): Observable<boolean> {
    const res = new Subject<boolean>();
    const { categoryId } = this.productForm.value;
    const { subCategoryId } = this.productForm.value;
    const productList = this.productList.controls;

    // Sync selected products from form to tree
    if (productList && productList.length > 0) {
      const selectedProductIds = productList.map(
        (control) => control.value.productId
      );
      selectedProductIds.forEach((productId) => {
        this.selectedProductIds.add(productId);
      });
    }

    // If no categoryId, just return success
    if (!categoryId) {
      res.next(true);
      res.complete();
      return res;
    }

    // Use existing method to load categories (without prefill to avoid auto-selection)
    this._getCategoryList(false, false, insuranceType).subscribe({
      next: (categoriesLoaded) => {
        if (!categoriesLoaded) {
          res.next(false);
          res.complete();
          return;
        }

        // If categoryId is set, load subcategories
        if (categoryId && subCategoryId) {
          this._getSubCategoryList(categoryId, false, false).subscribe({
            next: (subcategoriesLoaded) => {
              if (!subcategoriesLoaded) {
                res.next(false);
                res.complete();
                return;
              }

              // If subCategoryId is set, load products
              if (subCategoryId) {
                this._getProductList(subCategoryId, false, false).subscribe({
                  next: (productsLoaded) => {
                    if (productsLoaded) {
                      // Sync selected products to tree after products are loaded
                      // This will preserve existing amounts in the form array
                      this.syncSelectedProductsToTree();
                      if (applyProductFusion) {
                        this.applyProductFusionAfterImport();
                      }
                    }
                    res.next(productsLoaded);
                    res.complete();
                  },
                  error: () => {
                    res.next(false);
                    res.complete();
                  },
                });
              } else {
                res.next(true);
                res.complete();
              }
            },
            error: () => {
              res.next(false);
              res.complete();
            },
          });
        } else {
          res.next(true);
          res.complete();
        }
      },
      error: () => {
        res.next(false);
        res.complete();
      },
    });

    return res;
  }

  resetProductSelection() {
    this.productData = [];
    this.productForm.controls['product'].reset();
    this.productList.clear();
    this.searchFormControl.reset('', { emitEvent: false });
    this.filteredProductTreeData = [];
    this.productTreeData = [];
    this.filteredProductTreeData = [];
    this.selectedProductIds.clear();
  }

  searchFormControl: FormControl<string> = new FormControl<string>('');

  searchSubscription: Subscription;

  // The form control (add-contract-modal only) feeds the input binding but is
  // set before change detection updates the input, so it is read first.
  getInsuranceType() {
    return (
      this.productForm.controls['insuranceType']?.value ||
      this.insuranceType ||
      null
    );
  }

  /**
   * Applies product fusion logic after import: appends subproduct names to main products.
   * This ensures imported products follow the same display logic as manually selected products.
   */
  private applyProductFusionAfterImport(): void {
    if (!this.productList || this.productList.length === 0) {
      return;
    }

    // Group subproducts by their mainProductId
    // Subproducts are products where mainProductId !== rootProductId
    const productsByMainProduct = new Map<
      string,
      FormGroup<ProductItemFormGroup>[]
    >();

    this.productList.controls.forEach((control) => {
      const product = control.value;
      if (
        product.mainProductId &&
        product.rootProductId &&
        product.mainProductId !== product.rootProductId
      ) {
        if (!productsByMainProduct.has(product.mainProductId)) {
          productsByMainProduct.set(product.mainProductId, []);
        }
        productsByMainProduct
          .get(product.mainProductId)!
          .push(control as FormGroup<ProductItemFormGroup>);
      }
    });

    // Update main product display names with appended sub-product names
    // Only use the first subproduct if multiple exist (only one subproduct per main product allowed)
    productsByMainProduct.forEach((subProductControls, mainProductId) => {
      const mainProductIndex = this.productList.controls.findIndex(
        (control) => control.value.productId === mainProductId
      );

      if (mainProductIndex !== -1 && subProductControls.length > 0) {
        const mainProductControl = this.productList.controls[
          mainProductIndex
        ] as FormGroup<ProductItemFormGroup>;
        const mainProduct = mainProductControl.value;

        // Start with the original main product name
        let mainProductName =
          this.lngCode === 'en'
            ? (mainProduct.productName ?? mainProduct.productNameInFrench)
            : (mainProduct.productNameInFrench ?? mainProduct.productName);

        // Only append the first sub-product name (only one subproduct per main product allowed)
        const firstSubProduct = subProductControls[0].value;
        const subProductName =
          this.lngCode === 'en'
            ? (firstSubProduct.productName ??
              firstSubProduct.productNameInFrench)
            : (firstSubProduct.productNameInFrench ??
              firstSubProduct.productName);
        mainProductName = `${mainProductName} - ${subProductName}`;

        mainProductControl.patchValue(
          {
            displayProductName: mainProductName,
          },
          { emitEvent: false }
        );
      }
    });
  }
}
